/* Curated gallery: consumes explicit option mappings; never modifies the product form. */
(() => {
  'use strict';
  if (customElements.get('enr-product-gallery')) return;

  function matchingIndex(items, options) {
    let best = -1;
    let score = 0;
    items.forEach((item, index) => {
      const entries = Object.entries(item.optionValues || {});
      if (entries.length > score && entries.every(([name, value]) => String(options[name]) === String(value))) {
        best = index;
        score = entries.length;
      }
    });
    return best >= 0 ? best : Math.max(0, items.findIndex(item => !Object.keys(item.optionValues || {}).length));
  }

  class EnrProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      let manifest;
      try { manifest = JSON.parse(this.querySelector('[data-gallery-data]').textContent); } catch (_) { return; }
      this.items = (Array.isArray(manifest?.items) ? manifest.items : []).filter(item => item && typeof item.src === 'string' && /^(https?:\/\/|\/\/|\/)/.test(item.src));
      if (!this.items.length) return;
      this.variants = Array.isArray(manifest.variants) ? manifest.variants : [];
      this.selectedVariantKey = null;
      this.abort = new AbortController();
      const listen = (element, event, handler, options = {}) => element.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      this.image = this.querySelector('[data-gallery-image]');
      this.stage = this.querySelector('[data-gallery-stage]');
      this.caption = this.querySelector('[data-gallery-caption]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.thumbs = this.querySelector('[data-gallery-thumbnails]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.zoomImage = this.querySelector('[data-gallery-zoom-image]');
      this.zoomArea = this.querySelector('[data-gallery-zoom-area]');
      this.zoomButton = this.querySelector('[data-gallery-zoom]');
      this.thumbs.replaceChildren();
      this.items.forEach((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'enr-product-gallery__thumbnail';
        button.setAttribute('aria-label', `תמונה ${index + 1}: ${item.alt || this.dataset.productTitle}`);
        const image = document.createElement('img');
        image.src = item.thumbSrc || item.src;
        image.alt = '';
        image.width = 76;
        image.height = 76;
        image.loading = 'lazy';
        button.append(image);
        listen(button, 'click', () => this.show(index));
        this.thumbs.append(button);
      });
      this.querySelectorAll('[data-gallery-enhanced]').forEach(element => { element.hidden = false; });
      this.thumbs.hidden = this.items.length < 2;
      this.querySelectorAll('[data-gallery-prev],[data-gallery-next]').forEach(button => { button.disabled = this.items.length < 2; });
      listen(this.querySelector('[data-gallery-prev]'), 'click', () => this.show(this.index - 1));
      listen(this.querySelector('[data-gallery-next]'), 'click', () => this.show(this.index + 1));
      listen(this, 'keydown', event => {
        if (this.dialog.open || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : this.index + (event.key === 'ArrowLeft' ? 1 : -1);
        this.show(next);
        if (this.thumbs.contains(event.target)) this.thumbs.children[this.index].focus({ preventScroll: true });
      });
      listen(this.stage, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' && event.isPrimary) this.pointer = { x: event.clientX, y: event.clientY };
      });
      listen(this.stage, 'pointercancel', () => { this.pointer = null; });
      listen(this.stage, 'pointerup', event => {
        if (!this.pointer) return;
        const x = event.clientX - this.pointer.x;
        const y = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.3) {
          this.suppressClickUntil = Date.now() + 500;
          this.show(this.index + (x > 0 ? 1 : -1));
        }
      });
      listen(this.stage, 'click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); return; }
        if (typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.updateZoom();
        this.dialog.showModal();
      });
      listen(this.querySelector('[data-gallery-close]'), 'click', () => this.dialog.close());
      listen(this.dialog, 'close', () => {
        this.zoomArea.classList.remove('is-zoomed');
        this.zoomButton.setAttribute('aria-pressed', 'false');
        this.zoomButton.textContent = 'הגדלה +';
        this.stage.focus({ preventScroll: true });
      });
      listen(this.zoomButton, 'click', () => {
        const zoomed = this.zoomArea.classList.toggle('is-zoomed');
        this.zoomButton.setAttribute('aria-pressed', String(zoomed));
        this.zoomButton.textContent = zoomed ? 'הקטנה −' : 'הגדלה +';
      });
      listen(document, 'variant:change', event => {
        // Product forms contain input[name="id"], which shadows form.id.
        if (event.target?.getAttribute?.('id') !== this.dataset.formId) return;
        if (event.detail?.variant?.id == null) return;
        this.selectVariant(event.detail?.variant?.id);
      });
      const form = document.getElementById(this.dataset.formId);
      this.selectVariant(form?.elements?.namedItem('id')?.value || this.dataset.variantId);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.dialog?.open) this.dialog.close();
    }

    selectVariant(id) {
      const key = String(id ?? '');
      if (this.selectedVariantKey === key) return;
      this.selectedVariantKey = key;
      const variant = this.variants.find(item => String(item.id) === String(id));
      this.show(matchingIndex(this.items, variant?.options || {}));
    }

    show(index) {
      this.index = (index + this.items.length) % this.items.length;
      const item = this.items[this.index];
      this.image.src = item.src;
      this.image.alt = item.alt || this.dataset.productTitle;
      this.stage.href = item.src;
      this.caption.textContent = item.caption || 'תמונת המחשה של המוצר. הדגם והכמות נקבעים לפי האפשרות שבחרתם.';
      this.counter.textContent = `תמונה ${this.index + 1} מתוך ${this.items.length}`;
      Array.from(this.thumbs.children).forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
      const active = this.thumbs.children[this.index];
      if (active) {
        const rect = active.getBoundingClientRect();
        const parent = this.thumbs.getBoundingClientRect();
        if (rect.left < parent.left || rect.right > parent.right) this.thumbs.scrollBy({ left: rect.left - parent.left - (parent.width - rect.width) / 2, behavior: 'instant' });
      }
      if (this.dialog.open) this.updateZoom();
    }

    updateZoom() {
      const item = this.items[this.index];
      this.zoomImage.src = item.src;
      this.zoomImage.alt = item.alt || this.dataset.productTitle;
      this.querySelector('[data-gallery-dialog-caption]').textContent = this.caption.textContent;
    }
  }
  customElements.define('enr-product-gallery', EnrProductGallery);
})();
